import { DataCard, SimpleCard } from "./DataCard";
import { BarChart } from "./BarChart";
import { Globe, Mail, TrendingUp, AlertTriangle } from "lucide-react";

const decemberFindings = [
  {
    tag: "Gmail performance",
    stat: "89.8% → 87.2%",
    statLabel: "inbox rate drop",
    description: "Validity reports Gmail inbox placement dropped from 89.8% at start of 2024 to 87.2% by Q4, after new bulk sender rules requiring SPF/DKIM/DMARC and 0.3% spam thresholds.",
    bullets: [
      "Engagement quality now prioritized over volume.",
      "December faces the toughest filtering environment."
    ],
    icon: Mail
  },
  {
    tag: "Microsoft strictest",
    stat: "75.6%",
    statLabel: "inbox placement",
    description: "Microsoft (Outlook/Hotmail) has the lowest inbox placement at 75.6% among major providers due to aggressive AI-based filters and user feedback loops.",
    bullets: [
      "AI filtering evaluates tone and patterns.",
      "Cold outreach particularly affected."
    ],
    icon: AlertTriangle
  },
  {
    tag: "E-commerce benchmarks",
    stat: "32.67%",
    statLabel: "avg open rate",
    description: "MailerLite 2025 data shows e-commerce average open rate is 32.67% with just 1.07% click rate—significantly lower than less commercial industries like nonprofits (52.38%).",
    bullets: [
      "Retail emails face highest competition.",
      "Holiday promotions amplify this disparity."
    ],
    icon: TrendingUp
  },
  {
    tag: "Spam threshold",
    stat: "0.3%",
    statLabel: "complaint limit",
    description: "Google and Yahoo's 2024 requirements set a strict 0.3% spam complaint threshold. Exceeding this causes immediate redirection of all messages to spam folders.",
    bullets: [
      "Higher holiday volume increases complaint risk.",
      "List hygiene becomes critical in Q4."
    ],
    icon: Globe
  }
];

export const DecemberDeliverabilitySection = () => {
  return (
    <section className="section-card mt-5 sm:mt-7">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1.5 sm:gap-2 mb-5 sm:mb-6">
        <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight">
          December deliverability environment
        </h2>
        <p className="text-[11px] sm:text-xs text-muted-foreground">
          ISP-specific data from Validity 2024/2025
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
        {decemberFindings.map((finding, i) => (
          <DataCard key={i} {...finding} />
        ))}

        <SimpleCard title="Inbox placement by provider (2025)">
          <p className="text-xs text-muted-foreground leading-relaxed mb-4">
            Validity benchmark data shows significant variation between major mailbox providers.
          </p>

          <div className="space-y-3">
            <div>
              <div className="text-[10px] sm:text-[11px] text-muted-foreground mb-1.5 font-medium">
                Gmail: 87.2% inbox
              </div>
              <BarChart percentage={87} />
            </div>

            <div>
              <div className="text-[10px] sm:text-[11px] text-muted-foreground mb-1.5 font-medium">
                Yahoo/AOL: 86.0% inbox
              </div>
              <BarChart percentage={86} delay={100} />
            </div>

            <div>
              <div className="text-[10px] sm:text-[11px] text-muted-foreground mb-1.5 font-medium">
                Apple Mail: 76.3% inbox
              </div>
              <BarChart percentage={76} variant="red" delay={200} />
            </div>

            <div>
              <div className="text-[10px] sm:text-[11px] text-muted-foreground mb-1.5 font-medium">
                Microsoft: 75.6% inbox
              </div>
              <BarChart percentage={76} variant="red" delay={300} />
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground mt-3 pt-2 border-t border-border">
            Source: Validity 2024/2025 Email Deliverability Benchmark
          </p>
        </SimpleCard>

        <SimpleCard title="Why subscribers disengage">
          <p className="text-xs text-muted-foreground leading-relaxed mb-4">
            Industry research shows the primary drivers of unsubscribes and spam complaints.
          </p>

          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Emails not relevant</span>
                <span className="font-semibold text-destructive">40%</span>
              </div>
              <BarChart percentage={40} variant="red" />
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Content feels "spammy"</span>
                <span className="font-semibold text-destructive">28%</span>
              </div>
              <BarChart percentage={28} variant="red" delay={100} />
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Emails too frequent</span>
                <span className="font-semibold text-destructive">27%</span>
              </div>
              <BarChart percentage={27} variant="red" delay={200} />
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground mt-3 pt-2 border-t border-border">
            Source: Email marketing industry benchmarks 2024
          </p>
        </SimpleCard>
      </div>
    </section>
  );
};